import { useCallback, useEffect, useState } from 'react';
import { CalendarClock } from 'lucide-react';
import { api, ErrorApi } from '../api';
import Ayuda from '../ui/Ayuda';
import { Cargando, ErrorConReintento } from '../ui/EstadoDePantalla';
import { cuandoCaduca, fechaDelDia } from './vigenciaDePermiso';

// AC-10 (1 oct 2026) · LOS PERMISOS DADOS APARTE QUE CADUCAN PRONTO.
//
// Un permiso con fecha de fin se retira solo. La persona se entera el día
// que un botón deja de estar; quien lo concedió, si mira aquí, lo ve venir
// y decide: dejarlo caducar o darlo otra vez con otra fecha.
//
// Es de quien administra: la ruta contesta 403 a los demás y entonces la
// sección no se pinta (un control visible funciona o no se ve).
//
// `vuelta` cambia cuando la pantalla de Personal recarga —por ejemplo,
// tras dar un permiso en bloque—, y la lista se vuelve a pedir.
export const DIAS_VENTANA = 14;

export default function PermisosQueCaducan({ vuelta = 0 }) {
  const [datos, setDatos] = useState(null);
  const [estado, setEstado] = useState('cargando'); // cargando | listo | error | oculto
  const [error, setError] = useState(null);

  // Solo cambia el estado en la respuesta (nada síncrono dentro del
  // efecto). Al volver a pedir tras aplicar algo en bloque, la lista vieja
  // se queda a la vista hasta que llega la nueva; el «Reintentar» sí
  // enseña el indicador de carga.
  const pedir = useCallback(() => {
    return api.get(`/permisos/caducan-pronto?dias=${DIAS_VENTANA}`)
      .then((d) => { setDatos(d); setEstado('listo'); })
      .catch((e) => {
        if (e instanceof ErrorApi && (e.status === 403 || e.status === 404)) {
          setEstado('oculto');
        } else {
          setError(e?.message || String(e));
          setEstado('error');
        }
      });
  }, []);

  useEffect(() => { pedir(); }, [pedir, vuelta]);

  if (estado === 'oculto') return null;

  const dias = datos?.dias ?? DIAS_VENTANA;
  const lista = datos?.permisos || [];

  return (
    <section aria-labelledby="permisos-caducan-titulo" className="space-y-2">
      <h3 id="permisos-caducan-titulo"
          className="flex items-center gap-2 text-sm font-semibold text-gray-800">
        <CalendarClock className="h-4 w-4" aria-hidden="true" />
        Permisos que caducan en los próximos {dias} días
      </h3>
      <Ayuda id="seguridad.permisos_caducan.pantalla" />

      {estado === 'cargando' && <Cargando filas={2} etiqueta="Cargando los permisos que caducan" />}
      {estado === 'error' && (
        <ErrorConReintento que="No se pudo cargar la lista de permisos que caducan"
                           porque={error}
                           onReintentar={() => { setEstado('cargando'); pedir(); }} />
      )}

      {estado === 'listo' && lista.length === 0 && (
        <p className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-900">
          Ningún permiso dado aparte caduca en los próximos {dias} días.
          Para dar uno con fecha de fin: marca a las personas, pulsa
          «Acciones para los seleccionados» y elige «Dar un permiso».
        </p>
      )}

      {estado === 'listo' && lista.length > 0 && (
        <ul className="divide-y divide-amber-100 rounded-lg border border-amber-200 bg-amber-50">
          {lista.map((p) => (
            <li key={`${p.id_usuario}-${p.codigo}`} className="p-3 text-sm text-amber-950">
              <p>
                <strong>{p.nombre_completo}</strong> · {p.nombre}
              </p>
              <p className="text-xs text-amber-900">
                Vale hasta el {fechaDelDia(p.vigente_hasta)}: {cuandoCaduca(p.dias_restantes)}.
                {p.concedido_por ? ` Se lo dio ${p.concedido_por}.` : ''}
              </p>
            </li>
          ))}
        </ul>
      )}
      {estado === 'listo' && lista.length > 0 && <Ayuda id="seguridad.permisos_caducan.renovar" />}
    </section>
  );
}
